// src/components/IncompleteRegistrationDialog.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faArrowRight,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@iconify/react';

interface IncompleteRegistrationDialogProps {
  type: 'quote' | 'carrier';
  onContinue: () => void;
  onDiscard: () => void;
  onClose?: () => void;
}

const IncompleteRegistrationDialog: React.FC<IncompleteRegistrationDialogProps> = ({
  type,
  onContinue,
  onDiscard,
  onClose,
}) => {
  const typeText = type === 'quote' ? 'quote' : 'carrier registration';
  
  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full text-[#003380] hover:text-[#002660] hover:bg-gray-100 transition-colors"
          >
            <Icon icon="mdi:close" className="w-5 h-5" />
          </button>
        )}
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
              <FontAwesomeIcon 
                icon={faExclamationTriangle} 
                className="text-yellow-600 text-xl" 
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {type === 'quote' ? 'Pending Request Found' : 'Existing Registration Found'}
              </h3>
              <p className="text-sm text-gray-600">
                {type === 'quote' 
                  ? `You have an incomplete ${typeText}`
                  : `You already have a ${typeText}`
                }
              </p>
            </div>
          </div>

          <p className="text-gray-700 mb-6">
            {type === 'quote' 
              ? `We found a ${typeText} request that you haven't completed payment for. What would you like to do?`
              : `We found an existing ${typeText} in our system. You can update your information or create a new registration.`
            }
          </p>

          <div className="space-y-3">
            <button
              onClick={onContinue}
              className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faArrowRight} className="mr-2" />
              {type === 'quote' 
                ? 'Continue with your previous request'
                : 'Update existing registration'
              }
            </button>

            <button
              onClick={onDiscard}
              className="w-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              {type === 'quote' 
                ? 'Discard previous request and create new one'
                : 'Replace with new registration'
              }
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            {type === 'quote' 
              ? 'If you continue with the previous request, the button will change to "Update and Pay"'
              : 'If you continue with the previous request, the button will change to "Update Registration"'
            }
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default IncompleteRegistrationDialog;